/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Thu Aug 03 2023
 **/

import { useEffect } from "react";
import { UserCredential } from "firebase/auth";
import { useAtomValue } from "jotai";
import { onMessage, MessagePayload } from "firebase/messaging";

import { JotaiStore } from "@/frameworks/libraries/commons";

import { FirebaseStateAtom } from "./atom";
import SingleTonFirebaseServiceController from "../../firebase/controller/FirebaseServiceController";

type FirebaseOption = Partial<{
	onSuccess: (result?: UserCredential, idp?: "GOOGLE" | "APPLE") => void;
	onError: <E = unknown>(error?: E) => void;
	onMessage: (payload: MessagePayload) => void;
	onTokenSuccess: (token?: string) => void;
}>;

/**
 * @description useFirebaseAction는 FirebaseProvider 하위에서 사용되는 hook입니다.
 */
export default function useFirebaseAction(options?: FirebaseOption) {
	const firebaseApp = useAtomValue(FirebaseStateAtom, {
		store: JotaiStore
	});

	useEffect(() => {
		SingleTonFirebaseServiceController.getInstance().clientToken$.subscribe((token) => {
			token && options?.onTokenSuccess?.(token);
		});
	}, []);

	useEffect(() => {
		if (options && options.onMessage && firebaseApp) {
			onMessage(firebaseApp.messaging, (payload: MessagePayload) => {
				// console.debug(LOGTAG, "onMessage", payload);
				options?.onMessage?.(payload);
			});
		}
	}, []);

	return {
		handleLogin: (idp: "GOOGLE" | "APPLE") => {
			SingleTonFirebaseServiceController.getInstance().handlelogin(idp, {
				onSuccess: (result, idp) => {
					options?.onSuccess?.(result, idp);
				},
				onError: (error) => {
					options?.onError?.(error);
				}
			});
		}
	};
}
